import React, {
	RefAttributes,
	memo,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from "react";
import { react } from "uikit";
import MultiSelect from "../../../../../../components/common/LocalMultiSelect";
import { useTranslation } from "react-i18next";

import PaymentSystem from "../../../../../../services/PaymentSystem";
import {
	useTypedDispatch,
	useTypedSelector,
} from "../../../../../../redux/store";
import getCompanies from "../../../../../../redux/services/Company/getCompanies";
import useObjectEditor from "../../../../../../hooks/useObjectEditor";
import LabeledField from "../../../../../../components/LabeledField";
import { useTaxiServiceFilterAccessControl } from "../../../../../../hooks";
import translationPath from "../../constants/translationPath";

import InternalController from "./Controller";

const CompaniesAndTaxiServicesBase = react.withController<
	CompaniesAndTaxiServices.PropsBase,
	CompaniesAndTaxiServices.Controller
>(({ value, onChange, controller }) => {
	const dispatch = useTypedDispatch();
	const { t } = useTranslation();
	const { subCompaniesOptions, subTaxiServiceOptions } =
		useTaxiServiceFilterAccessControl<any>();

	const companies = useTypedSelector(
		(state) => state.ordersPageReducer.companies,
	);

	const valueEditor = useObjectEditor(value, onChange);

	const taxiServiceIds = valueEditor.useGetter("taxiServiceIds");
	const setTaxiServiceIds = valueEditor.useSetter("taxiServiceIds");

	const selectedCompanies = valueEditor.useGetter("companyIds");
	const setSelectedCompanies = valueEditor.useSetter("companyIds");

	const [initialCompanyIds] = useState(selectedCompanies);
	const [initialTaxiServiceIds] = useState(taxiServiceIds);
	const [error, setError] = useState(false);

	useEffect(() => {
		dispatch(getCompanies());
	}, [dispatch]);

	const companiesOptions = useMemo(
		() => subCompaniesOptions({ initialCompanyIds }),
		[subCompaniesOptions, initialCompanyIds],
	);

	const taxiServiceOptions = useMemo(
		() =>
			subTaxiServiceOptions({
				initialTaxiServiceIds,
				initialCompanyIds,
				selectedCompanies,
			}),
		[
			subTaxiServiceOptions,
			initialTaxiServiceIds,
			initialCompanyIds,
			selectedCompanies,
		],
	);

	const updateSelectedValues = useCallback(
		(newCompanies) => {
			setSelectedCompanies(newCompanies);
			const newTaxiServiceIds = taxiServiceIds.filter((serviceId) => {
				const serviceCompany = companies.items.find((company) =>
					company.taxiServices.some(
						(service) => service.id === serviceId,
					),
				);
				return (
					serviceCompany && newCompanies.includes(serviceCompany.id)
				);
			});
			setTaxiServiceIds(newTaxiServiceIds);
		},
		[
			setSelectedCompanies,
			taxiServiceIds,
			setTaxiServiceIds,
			companies.items,
		],
	);

	useEffect(() => {
		if (companiesOptions.length === 1)
			updateSelectedValues([companiesOptions[0].key]);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [companiesOptions]);

	useEffect(() => {
		if (taxiServiceOptions.length === 1)
			setTaxiServiceIds([taxiServiceOptions[0].key]);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [taxiServiceOptions]);

	controller.setContext({ value, setError });

	return (
		<>
			<LabeledField
				label={t(`${translationPath}.modal.company.title`) || ""}
			>
				<MultiSelect
					all={companiesOptions.length > 1}
					disabled={companiesOptions.length === 1}
					placeholder={
						t(`${translationPath}.modal.company.chooseCompany`) ||
						""
					}
					error={error}
					value={selectedCompanies}
					options={companiesOptions}
					onChange={updateSelectedValues}
				/>
			</LabeledField>
			<LabeledField
				label={t(`${translationPath}.modal.branch.title`) || ""}
			>
				<MultiSelect
					all={taxiServiceOptions.length > 1}
					disabled={taxiServiceOptions.length <= 1}
					placeholder={
						t(`${translationPath}.modal.branch.chooseBranch`) || ""
					}
					value={taxiServiceIds}
					error={error}
					options={
						taxiServiceOptions.length === 0
							? undefined
							: taxiServiceOptions
					}
					onChange={(ids) => {
						setTaxiServiceIds(ids as number[]);
					}}
				/>
			</LabeledField>
		</>
	);
}, InternalController);
const CompaniesAndTaxiServices = memo(CompaniesAndTaxiServicesBase);

declare namespace CompaniesAndTaxiServices {
	type Ref = InternalController | null;
	type Controller = InternalController;
	interface PropsBase {
		value: Value;
		onChange: (x: Value) => void;
	}
	type Props = PropsBase & RefAttributes<Ref>;

	interface Value {
		taxiServices: PaymentSystem.Model["taxiServices"];
		taxiServiceIds: PaymentSystem.Model["taxiServiceIds"];
		companies: PaymentSystem.Model["companies"];
		companyIds: PaymentSystem.Model["companyIds"];
	}
}

export default CompaniesAndTaxiServices;
